/* eslint-disable react-refresh/only-export-components */
import * as React from "react";
import { CalendarIcon, ChevronDown, Zap } from "lucide-react";
import { generateText, LanguageModel, Output } from "ai";
import { createOpenRouter } from "@openrouter/ai-sdk-provider";
import { z } from "zod";

import { cn } from "@/lib/utils";
import { trackError } from "@/elements/lib/errorTracking";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TimeRange {
  from: Date;
  to: Date;
}

export type DateRangePreset =
  | "15m"
  | "1h"
  | "4h"
  | "1d"
  | "2d"
  | "3d"
  | "7d"
  | "15d"
  | "30d"
  | "90d";

export interface TimeRangePreset {
  label: string;
  shortLabel: string;
  value: DateRangePreset;
  getRange: () => TimeRange;
}

// ---------------------------------------------------------------------------
// Date Utilities (no external dependencies)
// ---------------------------------------------------------------------------

function formatDate(date: Date, pattern: "short" | "medium" = "short"): string {
  if (pattern === "short") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Presets Configuration
// ---------------------------------------------------------------------------

export const PRESETS: TimeRangePreset[] = [
  {
    label: "Past 15 Minutes",
    shortLabel: "15m",
    value: "15m",
    getRange: () => ({
      from: new Date(Date.now() - 15 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 1 Hour",
    shortLabel: "1h",
    value: "1h",
    getRange: () => ({
      from: new Date(Date.now() - 60 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 4 Hours",
    shortLabel: "4h",
    value: "4h",
    getRange: () => ({
      from: new Date(Date.now() - 4 * 60 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 1 Day",
    shortLabel: "1d",
    value: "1d",
    getRange: () => ({
      from: new Date(Date.now() - 24 * 60 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 2 Days",
    shortLabel: "2d",
    value: "2d",
    getRange: () => ({
      from: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 3 Days",
    shortLabel: "3d",
    value: "3d",
    getRange: () => ({
      from: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 7 Days",
    shortLabel: "1w",
    value: "7d",
    getRange: () => ({
      from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 15 Days",
    shortLabel: "15d",
    value: "15d",
    getRange: () => ({
      from: new Date(Date.now() - 15 * 24 * 60 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 1 Month",
    shortLabel: "1mo",
    value: "30d",
    getRange: () => ({
      from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
      to: new Date(),
    }),
  },
  {
    label: "Past 3 Months",
    shortLabel: "3mo",
    value: "90d",
    getRange: () => ({
      from: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      to: new Date(),
    }),
  },
];

// Badge width class - shared between trigger and dropdown for alignment
const BADGE_WIDTH = "min-w-10";

export function getPresetRange(preset: DateRangePreset): TimeRange {
  const p = PRESETS.find((p) => p.value === preset);
  return p ? p.getRange() : PRESETS[5]!.getRange(); // Default to 3d
}

function getPresetByValue(value: DateRangePreset): TimeRangePreset | undefined {
  return PRESETS.find((p) => p.value === value);
}

// ---------------------------------------------------------------------------
// AI Time Range Parser
// ---------------------------------------------------------------------------

type ParseResult =
  | { type: "preset"; preset: DateRangePreset }
  | { type: "custom"; range: TimeRange; label?: string }
  | null;

const MONTH_INDEX_BY_NAME: Readonly<Record<string, number>> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};
const SINCE_DATE_PATTERN =
  /^since\s+([a-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s+(\d{4}))?$/i;

/**
 * Resolve common calendar-relative ranges locally.
 *
 * These inputs are deterministic and should not depend on the availability of
 * the AI parsing endpoint. More flexible natural-language input still falls
 * through to parseWithAI.
 */
export function parseRelativeTimeRange(
  input: string,
  now = new Date(),
): ParseResult {
  const normalizedInput = input.trim().replace(/\s+/g, " ").toLowerCase();

  if (normalizedInput === "this month") {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    return {
      type: "custom",
      range: { from, to: new Date(now) },
      label: from.toLocaleDateString("en-US", { month: "short" }),
    };
  }

  if (normalizedInput === "this year") {
    return {
      type: "custom",
      range: {
        from: new Date(now.getFullYear(), 0, 1),
        to: new Date(now),
      },
      label: String(now.getFullYear()),
    };
  }

  const sinceMatch = SINCE_DATE_PATTERN.exec(normalizedInput);
  if (!sinceMatch) return null;

  const month = MONTH_INDEX_BY_NAME[sinceMatch[1]!];
  const day = Number(sinceMatch[2]);
  const explicitYear = sinceMatch[3] ? Number(sinceMatch[3]) : null;
  if (month === undefined || day < 1 || day > 31) return null;

  let year = explicitYear ?? now.getFullYear();
  let from = new Date(year, month, day);

  // Without a year, "since Dec 1" in July means the most recent Dec 1.
  if (explicitYear === null && from.getTime() > now.getTime()) {
    year -= 1;
    from = new Date(year, month, day);
  }

  // Reject invalid dates (e.g. February 30) and explicit future ranges.
  if (
    from.getFullYear() !== year ||
    from.getMonth() !== month ||
    from.getDate() !== day ||
    from.getTime() > now.getTime()
  ) {
    return null;
  }

  return {
    type: "custom",
    range: { from, to: new Date(now) },
    label: `${month + 1}/${day}-${now.getMonth() + 1}/${now.getDate()}`,
  };
}

// Exported for unit testing. Decides how a successful parse is applied given
// the picker's available presets: `availablePresets` only limits the
// quick-pick options, so typed input that the AI normalizes to a preset
// (e.g. "last week" -> 7d) must still apply when that preset isn't offered —
// as the preset's concrete date range. Otherwise Enter silently no-ops
// (the billing page passes availablePresets={[]}).
export function resolveParsedRange(
  parsed: NonNullable<ParseResult>,
  isPresetAvailable: (preset: DateRangePreset) => boolean,
): NonNullable<ParseResult> {
  if (parsed.type === "preset" && !isPresetAvailable(parsed.preset)) {
    return {
      type: "custom",
      range: getPresetRange(parsed.preset),
      label: getPresetByValue(parsed.preset)?.shortLabel,
    };
  }
  return parsed;
}

const timeRangeSchema = z.object({
  from: z.string().describe("ISO8601 start date/time"),
  to: z.string().describe("ISO8601 end date/time"),
  label: z.string().describe("Short semantic label for the range"),
});

const TIME_RANGE_MODEL = "openai/gpt-5.4-mini";

/**
 * Parse an ISO date string as a local date (ignoring timezone).
 * This prevents timezone shifts when the AI returns dates like "2026-02-09T00:00:00Z"
 * which would otherwise display as Feb 8 in US timezones.
 */
function parseAsLocalDate(isoString: string): Date {
  // Try to extract just the date part and create a local date
  const dateMatch = isoString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dateMatch) {
    const year = dateMatch[1]!;
    const month = dateMatch[2]!;
    const day = dateMatch[3]!;
    // Check if there's a time component
    const timeMatch = isoString.match(/T(\d{2}):(\d{2}):?(\d{2})?/);
    if (timeMatch) {
      const hours = timeMatch[1]!;
      const minutes = timeMatch[2]!;
      const seconds = timeMatch[3] ?? "0";
      return new Date(
        parseInt(year),
        parseInt(month) - 1,
        parseInt(day),
        parseInt(hours),
        parseInt(minutes),
        parseInt(seconds),
      );
    }
    // Date only - use start of day local time
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  }
  // Fallback to standard parsing
  return new Date(isoString);
}

// Exported for unit testing of header/credential construction.
export async function parseWithAI(
  input: string,
  apiUrl: string,
  projectSlug?: string,
  authHeaders?: Record<string, string>,
): Promise<ParseResult> {
  const relativeRange = parseRelativeTimeRange(input);
  if (relativeRange) return relativeRange;

  try {
    const now = new Date();

    // Create OpenRouter provider without X-Gram-Source header (so usage is billed).
    // authHeaders carries the caller's session credential (e.g. Gram-Session /
    // Gram-Chat-Session). The /chat/completions proxy authenticates from these
    // request headers — relying on cookies alone fails (the Vercel AI SDK does
    // not forward them reliably), so a missing credential here 401s and the
    // catch below would silently swallow it.
    const headers: Record<string, string> = { ...authHeaders };
    if (projectSlug) {
      headers["Gram-Project"] = projectSlug;
    }

    const openRouter = createOpenRouter({
      baseURL: apiUrl,
      apiKey: "unused",
      headers,
      fetch: (url, init) =>
        fetch(url, {
          ...init,
          credentials: "include",
        }),
    });

    const model = openRouter.chat(TIME_RANGE_MODEL) as LanguageModel;

    const result = await generateText({
      model,
      output: Output.object({ schema: timeRangeSchema }),
      prompt: `You are a time range parser for an analytics dashboard. Parse natural language into a PAST time range.
Current time: ${now.toISOString()}

KEY RULES:
- "X days ago" = THE WHOLE DAY (from: start 00:00, to: end 23:59:59)
- "X months ago" = THE WHOLE MONTH (from: 1st 00:00, to: last day 23:59:59)
- "X years ago" = THE WHOLE YEAR (from: Jan 1, to: Dec 31)
- "past X days" = RANGE from X days ago to now
- "last wednesday" etc = that specific day (whole day)
- IMPORTANT: Return dates WITHOUT timezone suffix (no "Z"). Use format like "2026-02-09T00:00:00" not "2026-02-09T00:00:00Z"

LABEL RULES - use semantic labels:
- Duration presets: "15m", "1h", "4h", "1d", "2d", "3d", "7d", "15d", "30d"
- Single day: use 3-letter day name "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"
- Whole month: use 3-letter month name "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
- Whole year: use the year "2024", "2025"
- Date range: use "M/D-M/D" format like "1/5-1/10" or "12/1-12/15"

Examples:
- "4 days ago" -> label: "Mon" (or whatever day it was)
- "2 months ago" -> label: "Dec" (or whatever month)
- "1 year ago" -> label: "2025" (or whatever year)
- "past 3 days" -> label: "3d"
- "last wednesday" -> label: "Wed"
- "jan 5 to jan 10" -> label: "1/5-1/10"

User input: ${input}`,
    });

    const parsed = result.output;
    // Parse dates as local to avoid timezone shifts
    const from = parseAsLocalDate(parsed.from);
    const to = parseAsLocalDate(parsed.to);

    if (isNaN(from.getTime()) || isNaN(to.getTime())) {
      return null;
    }

    // Normalize labels like "1w" -> "7d", "2w" -> "14d"
    let normalizedLabel = parsed.label;
    if (normalizedLabel === "1w") normalizedLabel = "7d";
    if (normalizedLabel === "2w") normalizedLabel = "14d";
    if (normalizedLabel === "1mo") normalizedLabel = "30d";
    if (normalizedLabel === "3mo") normalizedLabel = "90d";

    const matchedPreset = PRESETS.find((p) => p.value === normalizedLabel);
    if (matchedPreset) {
      return { type: "preset", preset: matchedPreset.value };
    }

    // Use the semantic label from AI (e.g., "Mon", "Jan", "2024", "1/5-1/10")
    return { type: "custom", range: { from, to }, label: parsed.label };
  } catch (error) {
    // Returning null keeps the UI graceful on an unparseable string, but the
    // failure must not be invisible — a 401/gateway error here looks identical
    // to "couldn't parse" to the user. Surface it so it's diagnosable.
    trackError(error, {
      source: "custom",
      location: "TimeRangePicker.parseWithAI",
      hasAuthHeaders: Boolean(authHeaders),
    });
    return null;
  }
}

// ---------------------------------------------------------------------------
// TimeRangePicker Component (Datadog Style)
// ---------------------------------------------------------------------------

export interface TimeRangePickerProps {
  /** Current preset value */
  preset?: DateRangePreset | null;
  /** Current custom range */
  customRange?: TimeRange | null;
  /** Called when a preset is selected */
  onPresetChange?: (preset: DateRangePreset) => void;
  /** Called when a custom range is selected */
  onCustomRangeChange?: (from: Date, to: Date, label?: string) => void;
  /** Called to clear custom range */
  onClearCustomRange?: () => void;
  /** Initial label for custom range (from URL params) */
  customRangeLabel?: string | null;
  /** Show LIVE mode option */
  showLive?: boolean;
  /** Is LIVE mode active */
  isLive?: boolean;
  /** Called when LIVE mode changes */
  onLiveChange?: (isLive: boolean) => void;
  /** Presets to show in the dropdown */
  availablePresets?: readonly DateRangePreset[];
  /** Disabled state */
  disabled?: boolean;
  /** Timezone display; "UTC" also bounds the calendar by the current UTC day. */
  timezone?: string;
  /** API URL for AI parsing (defaults to window.location.origin) */
  apiUrl?: string;
  /** Project slug for API authentication */
  projectSlug?: string;
  /**
   * Auth headers to send with the AI parsing request to /chat/completions
   * (e.g. `{ "Gram-Session": token }`). The `/chat/completions` proxy
   * authenticates from request headers, not cookies, so without these the
   * request is rejected with 401 and natural-language parsing silently fails.
   */
  authHeaders?: Record<string, string>;
  /** Additional class name for the trigger */
  className?: string;
}

function TimeRangePicker({
  preset,
  customRange,
  onPresetChange,
  onCustomRangeChange,
  onClearCustomRange,
  customRangeLabel: initialCustomLabel,
  showLive = false,
  isLive = false,
  onLiveChange,
  availablePresets,
  disabled = false,
  timezone,
  apiUrl,
  projectSlug,
  authHeaders,
  className,
}: TimeRangePickerProps): React.JSX.Element {
  const [isOpen, setIsOpen] = React.useState(false);
  const [showCalendar, setShowCalendar] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [isEditing, setIsEditing] = React.useState(false);
  const [isParsing, setIsParsing] = React.useState(false);
  const [customLabel, setCustomLabel] = React.useState<string | null>(
    initialCustomLabel || null,
  );
  const inputRef = React.useRef<HTMLInputElement>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const now = new Date();
  let maxCalendarDate = now;
  if (timezone === "UTC") {
    maxCalendarDate = new Date(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
    );
  }

  // Sync custom label from props (e.g., when URL changes)
  React.useEffect(() => {
    if (initialCustomLabel !== undefined) {
      setCustomLabel(initialCustomLabel || null);
    }
  }, [initialCustomLabel]);

  const effectiveApiUrl =
    apiUrl || (typeof window !== "undefined" ? window.location.origin : "");
  const presetOptions = React.useMemo(() => {
    if (!availablePresets) return PRESETS;
    const available = new Set<DateRangePreset>(availablePresets);
    return PRESETS.filter((p) => available.has(p.value));
  }, [availablePresets]);
  const isPresetAvailable = React.useCallback(
    (presetValue: DateRangePreset) =>
      !availablePresets || availablePresets.includes(presetValue),
    [availablePresets],
  );

  const handlePresetClick = (p: TimeRangePreset) => {
    onPresetChange?.(p.value);
    setCustomLabel(null);
    setIsOpen(false);
    setInputValue("");
  };

  const handleLiveClick = () => {
    onLiveChange?.(!isLive);
    if (!isLive) {
      // When enabling LIVE, also select a default short preset
      if (isPresetAvailable("15m")) {
        onPresetChange?.("15m");
      }
    }
    setIsOpen(false);
  };

  const handleCalendarSelect = (range: { start: Date; end: Date | null }) => {
    if (range.start && range.end) {
      onCustomRangeChange?.(range.start, range.end);
      setCustomLabel(null); // Calendar selections don't have AI labels
      setIsOpen(false);
      setShowCalendar(false);
      setInputValue("");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const applyParseResult = (parsed: ParseResult) => {
    if (parsed) {
      const resolved = resolveParsedRange(parsed, isPresetAvailable);
      if (resolved.type === "preset") {
        onPresetChange?.(resolved.preset);
        setCustomLabel(null);
      } else {
        const label = resolved.label || undefined;
        onCustomRangeChange?.(resolved.range.from, resolved.range.to, label);
        setCustomLabel(label || null);
      }
      setInputValue("");
      setIsOpen(false);
      setIsEditing(false);
      return true;
    }
    return false;
  };

  const handleInputKeyDown = async (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter" && inputValue.trim() && !isParsing) {
      // Use AI to parse natural language input
      setIsParsing(true);
      try {
        const aiParsed = await parseWithAI(
          inputValue,
          effectiveApiUrl,
          projectSlug,
          authHeaders,
        );
        applyParseResult(aiParsed);
      } finally {
        setIsParsing(false);
      }
    } else if (e.key === "Escape") {
      setInputValue("");
      setIsEditing(false);
      setIsOpen(false);
    } else if (e.key === "Backspace" && inputValue === "" && customRange) {
      // Clear custom range when backspacing on empty input
      e.preventDefault();
      onClearCustomRange?.();
    }
  };

  const handleInputClick = (e: React.MouseEvent) => {
    // Prevent the popover trigger from toggling closed
    e.stopPropagation();
    setIsEditing(true);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsEditing(true);
    // Don't set isOpen here - let the click handler or popover manage it
  };

  const handleInputBlur = () => {
    // Delay to allow click events on dropdown items
    setTimeout(() => {
      if (!inputValue) {
        setIsEditing(false);
      }
    }, 150);
  };

  // Determine current range for display
  const currentRange = customRange ?? (preset ? getPresetRange(preset) : null);

  // Get short label for trigger badge
  const getShortLabel = () => {
    if (customRange) return customLabel || "Custom";
    if (preset) {
      const presetObj = getPresetByValue(preset);
      return presetObj?.shortLabel ?? preset;
    }
    return "7d";
  };

  // Get label text (preset label or custom range description)
  const getLabelText = () => {
    if (customRange) {
      return `${formatDate(customRange.from)} – ${formatDate(customRange.to)}`;
    }
    if (preset) {
      const presetObj = getPresetByValue(preset);
      return presetObj?.label ?? "Select time range";
    }
    return "Select time range";
  };

  const handleOpenChange = (open: boolean) => {
    // If closing while editing, keep it open unless explicitly closed via selection
    if (!open && isEditing) {
      return;
    }
    setIsOpen(open);
    if (open && inputRef.current) {
      // Focus input when opening
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        <div
          ref={triggerRef}
          className={cn(
            "relative inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-all outline-none",
            "border-border hover:border-border/80",
            disabled && "cursor-not-allowed opacity-50",
            timezone && "py-1.5",
            className,
          )}
        >
          {/* Floating timezone legend */}
          {timezone && (
            <span className="absolute -top-2 left-3 bg-background px-1 text-xs text-muted-foreground">
              {timezone}
            </span>
          )}

          {/* Short badge */}
          <span
            className={cn(
              "inline-flex h-6 items-center justify-center rounded px-2 py-1 text-xs font-semibold",
              BADGE_WIDTH,
              isLive
                ? "bg-green-500 text-white"
                : "bg-muted text-muted-foreground",
            )}
          >
            {isParsing ? (
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-current/30 border-t-current" />
            ) : (
              getShortLabel()
            )}
          </span>

          {/* Input field for natural language or display label */}
          <input
            ref={inputRef}
            type="text"
            value={isEditing ? inputValue : getLabelText()}
            onChange={handleInputChange}
            onClick={handleInputClick}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={(e) => {
              void handleInputKeyDown(e);
            }}
            placeholder="e.g., 3 days ago, last week..."
            disabled={disabled}
            className={cn(
              "min-w-35 flex-1 bg-transparent outline-none",
              "placeholder:text-muted-foreground/60",
              !isEditing && "cursor-pointer",
              disabled && "cursor-not-allowed",
            )}
          />

          {/* Dropdown chevron */}
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        </div>
      </PopoverTrigger>

      <PopoverContent
        className="w-fit max-w-[500px] p-0"
        align="end"
        collisionPadding={16}
        onOpenAutoFocus={(e) => {
          // Prevent popover from stealing focus from the input
          e.preventDefault();
          inputRef.current?.focus();
        }}
        onPointerDownOutside={(e) => {
          // handleOpenChange swallows close-while-editing so the input/popover
          // focus interplay can't dismiss mid-typing — but that makes a genuine
          // outside click take two clicks (first only blurs the input). Close
          // directly here, unless the click is on the trigger/input itself.
          // Inside a ShadowRoot embed, `e.target` is retargeted to the shadow
          // host, so check the original event's composedPath for the trigger
          // (same workaround as the tool-ui popover).
          const trigger = triggerRef.current;
          const path = e.detail.originalEvent.composedPath?.() ?? [];
          const target = e.target as Node | null;
          if (
            trigger &&
            (path.includes(trigger) || (target && trigger.contains(target)))
          ) {
            return;
          }
          setInputValue("");
          setIsEditing(false);
          setIsOpen(false);
        }}
      >
        <div className="flex flex-col">
          {/* Calendar view */}
          {showCalendar ? (
            <>
              <div className="flex items-center justify-between border-b border-border/50 px-3 py-2">
                <span className="text-xs font-medium text-muted-foreground">
                  Select date range
                </span>
                <button
                  type="button"
                  onClick={() => setShowCalendar(false)}
                  className="text-xs text-primary hover:underline"
                >
                  Back
                </button>
              </div>
              <Calendar
                selected={{
                  start: currentRange?.from ?? null,
                  end: currentRange?.to ?? null,
                }}
                onSelect={handleCalendarSelect}
                maxDate={maxCalendarDate}
              />
              {customRange && onClearCustomRange && (
                <div className="border-t border-border/50 p-2">
                  <button
                    type="button"
                    onClick={() => {
                      onClearCustomRange();
                      setShowCalendar(false);
                    }}
                    className="w-full text-xs text-muted-foreground transition-colors hover:text-foreground"
                  >
                    Clear custom range
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Presets list */
            <div className="py-1">
              {/* LIVE option */}
              {showLive && (
                <button
                  type="button"
                  onClick={handleLiveClick}
                  className={cn(
                    "flex w-full items-center gap-3 px-3 py-2 text-sm transition-colors",
                    "hover:bg-muted",
                    isLive && "bg-blue-500/10",
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center gap-1 rounded px-1.5 py-0.5 text-xs font-semibold",
                      BADGE_WIDTH,
                      isLive
                        ? "bg-green-500 text-white"
                        : "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400",
                    )}
                  >
                    <Zap className="h-3 w-3" />
                    LIVE
                  </span>
                  <span className="text-foreground/80">15 Minutes</span>
                </button>
              )}

              {/* Preset options */}
              {presetOptions.map((p) => {
                const isSelected =
                  preset === p.value && !customRange && !isLive;
                return (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => handlePresetClick(p)}
                    className={cn(
                      "flex w-full items-center gap-3 px-3 py-2 text-sm transition-colors",
                      isSelected ? "bg-blue-500 text-white" : "hover:bg-muted",
                    )}
                  >
                    <span
                      className={cn(
                        "inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-semibold",
                        BADGE_WIDTH,
                        isSelected
                          ? "bg-white/20 text-white"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      {p.shortLabel}
                    </span>
                    <span
                      className={
                        isSelected ? "text-white" : "text-foreground/80"
                      }
                    >
                      {p.label}
                    </span>
                  </button>
                );
              })}

              {/* Select from calendar */}
              <button
                type="button"
                onClick={() => setShowCalendar(true)}
                className={cn(
                  "flex w-full items-center gap-3 px-3 py-2 text-sm transition-colors",
                  customRange ? "bg-blue-500 text-white" : "hover:bg-muted",
                )}
              >
                <span
                  className={cn(
                    "inline-flex items-center justify-center rounded px-1.5 py-0.5",
                    BADGE_WIDTH,
                    customRange
                      ? "bg-white/20 text-white"
                      : "bg-muted text-muted-foreground",
                  )}
                >
                  <CalendarIcon className="h-4 w-4" />
                </span>
                <span
                  className={customRange ? "text-white" : "text-foreground/80"}
                >
                  Select from calendar...
                </span>
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
TimeRangePicker.displayName = "TimeRangePicker";

export { TimeRangePicker };
